import { jsx } from 'react/jsx-runtime';
import { AnimatePresence } from 'framer-motion';
import { useRef, useMemo } from 'react';
import { styled } from 'styled-components';
import { useInViewWithinRadixScrollArea } from '../ScrollArea.js';
import { useVirtualGrid } from './useVirtualGrid.js';
import { useDomContentRect } from '@fabric-app/hooks/useDomContentRect';

const VirtualGrid = ({ itemsCount, columnsCount: columnsCountProp = "auto", columnWidth: columnWidthProp, columnGap = 0, rowHeight, rowGap = 0, renderItem, containerWidth: containerWidthProp, zoomLevel = 1, scrollYMargin = 0 }) => {
  const containerRef = useRef(null);
  const isInView = useInViewWithinRadixScrollArea(containerRef);
  const [rect] = useDomContentRect(containerRef);
  const containerWidth = containerWidthProp || rect?.width || 0;
  const columnWidth = columnWidthProp || containerWidth;
  const enabled = isInView && containerWidth > 0;
  const columnsCount = columnsCountProp === "auto" ? Math.floor((containerWidth + columnGap) / (columnWidth + columnGap) / zoomLevel) : columnsCountProp;
  const { getVirtualItems, containerDimensions } = useVirtualGrid({
    itemHeight: Math.round(rowHeight * zoomLevel),
    itemWidth: Math.round(columnWidth * zoomLevel),
    itemsCount,
    columnCount: columnsCount,
    scrollElement: containerRef.current?.closest("[data-radix-scroll-area-viewport]"),
    scrollArea: containerRef.current?.closest("[data-radix-scroll-area-viewport]")?.parentElement,
    rowGap: Math.round(rowGap * zoomLevel),
    columnGap: Math.round(columnGap * zoomLevel),
    overscan: 5,
    scrollYMargin,
    enabled
  });
  const items = useMemo(() => getVirtualItems(), [
    getVirtualItems
  ]);
  return /* @__PURE__ */ jsx(VirtualGridContainer, {
    ref: containerRef,
    style: {
      height: containerDimensions.height,
      width: containerDimensions.width
    },
    children: items.map((virtualItem) => {
      const renderedContent = renderItem({
        index: virtualItem.index
      });
      if (renderedContent === null) {
        return null;
      }
      return /* @__PURE__ */ jsx(VirtualGridItem, {
        "data-index": virtualItem.index,
        "data-row": virtualItem.row,
        "data-column": virtualItem.column,
        style: {
          // original width and size without the zoom level
          width: virtualItem.width,
          height: virtualItem.height,
          transform: `translate3d(${virtualItem.startX}px, ${virtualItem.startY}px, 0)`
        },
        children: /* @__PURE__ */ jsx(InnerContainer, {
          style: {
            width: columnWidth,
            height: rowHeight,
            /**
            * zoom here with scale
            *
            * do not use css property zoom, as it doesn't play well with container queries
            * in safari and firefox. For the container-size queries, it takes the actual size instead of the original
            * without zoom
            */
            transform: `scale(${zoomLevel})`
          },
          children: /* @__PURE__ */ jsx(AnimatePresence, {
            children: renderedContent
          })
        })
      }, virtualItem.index);
    })
  });
};
const VirtualGridContainer = styled.div.withConfig({
  displayName: "VirtualGrid__VirtualGridContainer",
  componentId: "sc-a81cb849-0"
})([
  `position:relative;`
]);
const VirtualGridItem = styled.div.withConfig({
  displayName: "VirtualGrid__VirtualGridItem",
  componentId: "sc-a81cb849-1"
})([
  `position:absolute;top:0;left:0;display:flex;align-items:center;justify-content:center;&:hover{z-index:10;}`
]);
const InnerContainer = styled.div.withConfig({
  displayName: "VirtualGrid__InnerContainer",
  componentId: "sc-a81cb849-2"
})([
  `position:absolute;top:0;left:0;transform-origin:0 0;& > *{position:absolute;inset:0;}`
]);

export { VirtualGrid };
//# sourceMappingURL=VirtualGrid.js.map
